/**
 * BYOvsWizardModalPanel
 * Panel-layer BYO-vs-wizard lease creation choice modal.
 * Receives params: { unitId } from the WorkflowPanelProvider modal registry.
 *
 * Replicates the lease-choice modal from UnitDetailPage so it can be opened
 * from any screen via the workflow panel action registry.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../Modal';
import { getLeaseWizardDraft } from '../../../lib/api';
import type { LeaseDocumentDetailResponse } from '../../../types/lease-generation';

interface BYOvsWizardModalPanelProps {
  params: Record<string, string>;
  onClose: () => void;
}

export function BYOvsWizardModalPanel({ params, onClose }: BYOvsWizardModalPanelProps) {
  const { unitId } = params;
  const navigate = useNavigate();
  const [leaseDraft, setLeaseDraft] = useState<LeaseDocumentDetailResponse | null>(null);

  useEffect(() => {
    if (!unitId) return;
    getLeaseWizardDraft(unitId).then(setLeaseDraft).catch(() => setLeaseDraft(null));
  }, [unitId]);

  if (!unitId) return null;

  const openCreateLeaseWizard = () => {
    onClose();
    navigate(`/units/${unitId}/create-lease`);
  };

  const openCreateLeaseWizardFresh = () => {
    onClose();
    // Force step=1 so LeaseWizardPage starts fresh regardless of any stale draft.
    // "Resume Draft" uses openCreateLeaseWizard (no override); this path is "Generate with Wizard".
    navigate(`/units/${unitId}/create-lease?step=1`);
  };

  const openManualLeaseForm = () => {
    // BYO form is on UnitDetailPage — navigate there and the form state is internal.
    // The panel action only needs to open the choice; for BYO we navigate to the unit page.
    onClose();
    navigate(`/units/${unitId}?leaseForm=byo`);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Lease">
      <p className="text-sm text-muted-foreground mb-6">How would you like to create this lease?</p>
      <div className="flex flex-col gap-4">
        {leaseDraft && (
          <button
            type="button"
            onClick={openCreateLeaseWizard}
            className="flex flex-col items-start gap-2 p-5 border-2 border-warning/60 bg-warning/5 rounded-lg cursor-pointer hover:border-warning hover:bg-warning/10 transition-colors text-left min-h-[44px]"
            data-testid="lease-choice-resume-draft"
          >
            <span className="text-base font-semibold text-foreground">Resume Draft</span>
            <span className="text-sm text-muted-foreground">
              Continue where you left off — step {(leaseDraft.variables?.wizardCurrentStep as number | undefined) ?? 1} of 9
            </span>
          </button>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={openCreateLeaseWizardFresh}
            className="flex flex-col items-start gap-2 p-5 border-2 border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors text-left min-h-[44px]"
            data-testid="lease-choice-wizard"
            aria-label="Generate lease with Wizard"
          >
            <span className="text-base font-semibold text-foreground">Generate with Wizard</span>
            <span className="text-sm text-muted-foreground">Create a jurisdiction-aware lease with e-signing</span>
          </button>
          <button
            type="button"
            onClick={openManualLeaseForm}
            className="flex flex-col items-start gap-2 p-5 border-2 border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors text-left min-h-[44px]"
            data-testid="lease-choice-manual"
            aria-label="Register existing lease (Bring Your Own)"
          >
            <span className="text-base font-semibold text-foreground">Bring Your Own</span>
            <span className="text-sm text-muted-foreground">Register a lease that was created outside of Resida</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
